/**
 * The page header every surface renders: breadcrumb, title, the information
 * button, the scope notes, and the audit button where a record has a trail.
 *
 * One component rather than a header per screen, for the same reason the server
 * made it one include: a page that forgets the information button is a page
 * that returns 200 and answers no questions, and nothing in a status code sees
 * it. Making it structural means it cannot be forgotten on a new surface.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { Grant, SectionMeta } from '../api/types'
import { useSession } from '../app/session'
import { AuditButton } from './Audit'
import { ExplainButton } from './Explainer'
import { appPath } from '../app/paths'

export interface PageAction {
  label: string
  href?: string
  onClick?: () => void
  style?: 'primary' | 'danger' | ''
}

export function PageHead({ section, grant, title, subtitle, auditLink, actions,
                           extra }: {
  section?: SectionMeta | null
  grant?: Grant | { row_scope: string; requires_consent?: boolean;
                    requires_reason?: boolean } | null
  title?: ReactNode
  subtitle?: ReactNode
  auditLink?: string | null
  actions?: PageAction[]
  extra?: ReactNode
}) {
  const { t, nm, lbl, dir, boot } = useSession()

  return (
    <>
      <div className="crumb">
        <Link to={appPath(boot.landing)}>{t('nav.home', 'Home')}</Link>
        {section && (
          <>
            <span>›</span>{nm(section as unknown as Record<string, unknown>,
                              'module_name') || section.module_name}
            <span>›</span>{nm(section as unknown as Record<string, unknown>)}
          </>
        )}
      </div>

      <div className="page-head">
        <div>
          <h1>
            {title || (section
              ? nm(section as unknown as Record<string, unknown>)
              : boot.app_name)}
            {section && (
              <ExplainButton sectionKey={section.section_key} size="lg"
                             title={nm(section as unknown as Record<string, unknown>)} />
            )}
          </h1>

          {section && dir !== 'rtl' && section.name_ar && (
            <div className="ar" dir="rtl">{section.name_ar}</div>
          )}
          {subtitle && <div className="small muted">{subtitle}</div>}

          {section && (
            <div className="scope-note">
              <span className="b muted">{section.section_type}</span>
              {section.joget_element && (
                <span className="b info">Joget: {section.joget_element}</span>
              )}
              {grant && (
                <span className="b muted">row scope {lbl(grant.row_scope)}</span>
              )}
              {grant?.requires_consent && (
                <span className="b warn">consent gateway applied</span>
              )}
              {grant?.requires_reason && (
                <span className="b bad">reason recorded</span>
              )}
              {section.decision_affecting && (
                <span className="b warn">affects funding decisions</span>
              )}
              {section.build_state === 'placeholder' && (
                <span className="b warn">declared, not built · {section.release_phase}</span>
              )}
              {extra}
            </div>
          )}
        </div>

        <div className="grow" />

        <div className="page-actions">
          {(actions || []).map((a) => (
            a.href
              ? <Link key={a.label} className={`btn ${a.style || ''}`}
                      to={appPath(a.href)}>{a.label}</Link>
              : <button key={a.label} className={`btn ${a.style || ''}`}
                        type="button" onClick={a.onClick}>{a.label}</button>
          ))}
          {auditLink && (
            <AuditButton link={auditLink}>{t('drawer.audit_trail', 'Audit trail')}</AuditButton>
          )}
        </div>
      </div>
    </>
  )
}
